import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeContextType, ThemeMode } from '../types/theme';
import { getTheme, detectSystemTheme } from '../theme/theme';
import { DefaultTheme } from 'styled-components';

// 로컬 스토리지 키
const THEME_STORAGE_KEY = 'erp_theme_preference';

/**
 * ⛔ 어두운 화면은 잠시 끈다(2026-09-29 대표 결정).
 *    화면 바탕 대부분(디자인 토큰·Tailwind)은 밝은색 그대로인데, 어두운 테마를 켜면 GlobalStyle 의
 *    기본 글자색만 밝아져 판매 표·단추·제목·입력칸 글자가 흰 바탕에 사라졌다
 *    (DEV 실측: 판매 54곳 · 대시보드 12곳 · 대비 1.05 — 컴퓨터가 어두운 모드면 설정 없이도 자동으로 켜졌다).
 *    모든 화면이 어두운 바탕을 갖출 때까지 저장된 설정·컴퓨터 모드와 상관없이 밝은 화면으로 둔다.
 *    저장된 값(erp_theme_preference)은 지우지 않는다 — 다시 켤 때 이 값만 true 로 바꾼다.
 */
export const DARK_MODE_AVAILABLE: boolean = false;

// 초기 컨텍스트 값 - DefaultTheme 타입 사용
const noop = () => {
  /* 빈 함수 - 초기 컨텍스트용 */
};
const defaultContextValue: Omit<ThemeContextType, 'theme'> & { theme: DefaultTheme } = {
  theme: getTheme('light'),
  mode: 'light',
  toggleTheme: noop,
  setTheme: noop,
};

// 테마 컨텍스트 생성 - DefaultTheme 타입 사용
export const ThemeContext = createContext<
  Omit<ThemeContextType, 'theme'> & { theme: DefaultTheme }
>(defaultContextValue);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // 저장된 테마 가져오기 또는 시스템 테마 감지
  const getInitialTheme = (): ThemeMode => {
    if (!DARK_MODE_AVAILABLE) return 'light';
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme as ThemeMode;
    }

    return detectSystemTheme();
  };

  // 현재 테마 모드 상태
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme);

  // 테마 객체 - DefaultTheme 타입 사용
  const theme = useMemo<DefaultTheme>(() => getTheme(themeMode), [themeMode]);

  // 테마 설정
  const setTheme = useCallback((mode: ThemeMode) => {
    if (!DARK_MODE_AVAILABLE && mode === 'dark') return; // 꺼 둔 동안은 바꾸지도 저장하지도 않는다
    setThemeMode(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, []);

  // 테마 토글
  const toggleTheme = useCallback(() => {
    setTheme(themeMode === 'light' ? 'dark' : 'light');
  }, [themeMode, setTheme]);

  // 시스템 테마 변경 감지
  useEffect(() => {
    if (!DARK_MODE_AVAILABLE) return undefined; // 꺼 둔 동안은 컴퓨터 모드도 따르지 않는다
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // 로컬 스토리지에 저장된 테마가 없는 경우에만 시스템 테마 변경을 따름
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        setThemeMode(e.matches ? 'dark' : 'light');
      }
    };

    // 이벤트 리스너 등록
    mediaQuery.addEventListener('change', handleChange);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // 컨텍스트 값 - DefaultTheme 타입 사용
  const contextValue = useMemo(
    () => ({
      theme,
      mode: themeMode,
      toggleTheme,
      setTheme,
    }),
    [theme, themeMode, toggleTheme, setTheme],
  );

  // StyledComponents에 테마 제공 및 컨텍스트 제공
  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// 테마 컨텍스트 훅
export const useTheme = () => {
  const context = React.useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme는 ThemeProvider 내에서 사용해야 합니다.');
  }

  return context;
};

export default ThemeProvider;
